import * as React from "react";
import {
  Navbar,
  ButtonToolbar,
  Button,
  Alert,
  Dropdown,
  OverlayTrigger,
  Form,
  Container,
  Card,
} from "react-bootstrap";
import I18n from "lib/I18n";
import { Loader } from "lib/components/Loader";
import { DiffEditor } from "@monaco-editor/react";
import style from "./style.module.css";
import { HelpButton } from "./HelpButton";
import { envColors } from "../../constants";
import tooltips from "../../lib/tooltips";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { observer } from "mobx-react-lite";
import { getStore } from "../../store";
import { useEffect } from "react";

export const Deployment = observer(() => {
  const { env, ddl, list, jobStatus } = getStore();

  useEffect(() => {
    ddl.getDdl();
    jobStatus.getDeployStatus();
  }, []);

  const onClickReload = (e: React.FormEvent<HTMLElement>) => {
    e.stopPropagation();
    ddl.getDdl();
    jobStatus.getDeployStatus();
  };

  const onClickDeploy = async (e: React.FormEvent<HTMLElement>) => {
    e.stopPropagation();
    if (env.isLp1()) {
      if (!confirm(I18n.t("event_master_editor.messages.confirm_lp1_action"))) {
        return false;
      }
    }
    await jobStatus.postDeploy();
    ddl.getDdl();
    jobStatus.getDeployStatus();
  };

  const onChangeEnv = (value: string | null) => {
    if (value == null) {
      return;
    }
    env.selected = value;
  };

  const envColor = (env: string) => {
    return envColors[env] || "primary";
  };

  const renderEnvItems = () => {
    return env.list.map((env) => (
      <Dropdown.Item key={env} eventKey={env}>
        {env}
      </Dropdown.Item>
    ));
  };

  const renderNotAvailable = () => {
    return (
      <div className={style.root}>
        <Alert variant="danger">
          {I18n.t("event_master_editor.errors.bigquery_not_available")}
        </Alert>
      </div>
    );
  };

  const renderMain = () => {
    return (
      <div className={style.root}>
        <Container fluid>
          <Navbar>
            <div className="me-auto">
              <Form>
                <ButtonToolbar>
                  <Dropdown className="me-2" onSelect={onChangeEnv}>
                    <Dropdown.Toggle
                      id="envdropdown"
                      variant={envColor(env.selected)}
                      title={env.selected}
                      key={env.selected}
                    >
                      {env.selected}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>{renderEnvItems()}</Dropdown.Menu>
                  </Dropdown>
                  <Button className="me-2" onClick={onClickReload}>
                    {I18n.t("event_master_editor.action.reload")}
                  </Button>
                  <OverlayTrigger
                    placement="bottom"
                    defaultShow={!list.isSaved}
                    overlay={tooltips.save_before_deploy}
                    trigger={list.isSaved ? [] : ["hover"]}
                  >
                    <Button
                      className="me-2"
                      onClick={onClickDeploy}
                      disabled={!list.isSaved}
                    >
                      {I18n.t("event_master_editor.action.deploy")}
                    </Button>
                  </OverlayTrigger>
                  <HelpButton />
                </ButtonToolbar>
              </Form>
            </div>
          </Navbar>
        </Container>
        <Loader show={jobStatus.deploy.isLoading} style={{ height: "auto" }}>
          <Card className="mb-2">
            <Card.Header>
              {I18n.t("event_master_editor.label.deploy_job_status")}
            </Card.Header>
            <Card.Body>{jobStatus.deploy.value}</Card.Body>
          </Card>
        </Loader>
        <Loader
          show={ddl.isLoading}
          style={{ height: "auto", overflow: "hidden" }}
        >
          <Card className={`mb-2 ${style.diffCard}`}>
            <Card.Header>
              {I18n.t("event_master_editor.label.current_and_next_ddl")}
            </Card.Header>
            <Card.Body className={style.diffCardBody}>
              <DiffEditor
                language="json"
                theme="vs-light"
                options={{
                  readOnly: true,
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                }}
                original={ddl.current === "" ? "Not created" : ddl.current}
                modified={ddl.next}
              />
              <span className="text-muted">
                <BsFillInfoCircleFill />
                {I18n.t(
                  "event_master_editor.label.current_and_next_ddl_notice",
                )}
              </span>
            </Card.Body>
          </Card>
        </Loader>
      </div>
    );
  };

  if (!env.selected) {
    return renderNotAvailable();
  }
  return renderMain();
});
